import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { updateProjectDetails, updateProject } from '../../../utils/api';
import Button from '../../../components/Button';
import FormActions from '../../../components/FormActions';
import FormToggle from '../../../components/FormToggle';
import { formFieldClass, formShell } from '../../../styles/formShell';
import { thumbnailShell } from '../../../styles/thumbnailShell';
import { typeShell } from '../../../styles/typeShell';
import {
    prepareImageFileForUpload,
    IMAGE_CONVERT_ERROR_MESSAGE,
} from '../../../utils/prepareImageFileForUpload';
import { useToastContext } from '../../../App';

const ProjectDetailForm = ({ projectId, project, onUpdate, onSuccess, onError, onPublicModeSuccess, onPublicModeError, onUrlCopySuccess, onUrlCopyError }) => {
    const { showToast } = useToastContext();
    const [formData, setFormData] = useState({
        description: '',
        use_kml: false,
        is_public: false
    });
    const [originalFormData, setOriginalFormData] = useState({
        description: '',
        use_kml: false,
        is_public: false
    });
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState('');
    const [publicUrl, setPublicUrl] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasThumbnailChanged, setHasThumbnailChanged] = useState(false);
    const [thumbnailConverting, setThumbnailConverting] = useState(false);
    const [originalThumbnailUrl, setOriginalThumbnailUrl] = useState(null);
    const lastProjectIdRef = useRef(null);
    const lastProjectUpdateRef = useRef(null);

    useEffect(() => {
        if (!project) return;

        const initialData = {
            description: project.description || '',
            use_kml: project.use_kml || false,
            is_public: project.is_public || false
        };
        const currentThumbnailUrl = project.thumbnail_image_url || project.thumbnail_image || null;

        // プロジェクトIDが変更された場合
        const projectIdChanged = lastProjectIdRef.current !== project.id;

        // projectオブジェクトが更新されたかどうかをチェック（JSON文字列で比較）
        const projectKey = JSON.stringify({
            description: project.description,
            use_kml: project.use_kml,
            is_public: project.is_public,
            thumbnail_image_url: project.thumbnail_image_url || project.thumbnail_image
        });
        // 初期ロード時（lastProjectUpdateRef.currentがnull）はprojectUpdatedをfalseにする
        const projectUpdated = lastProjectUpdateRef.current !== null && lastProjectUpdateRef.current !== projectKey;

        // プロジェクトIDが変更された場合、または初期ロード時、またはprojectが更新された場合
        if (projectIdChanged || lastProjectUpdateRef.current === null || projectUpdated) {
            // フォームデータとoriginalFormDataを更新
            setFormData(initialData);
            setOriginalFormData(initialData);
            setPublicUrl(project.public_url || '');

            // サムネイルURLの更新
            // currentThumbnailUrlがnullの場合でも、thumbnailPreviewがdata:URLで始まる場合は保持する
            // これは、onUpdate後にprojectが更新されるが、thumbnail_image_urlがnullになっている場合の対処
            let thumbnailUrlSet = false; // originalThumbnailUrlが設定されたかどうかのフラグ
            if (currentThumbnailUrl) {
                setThumbnailPreview(currentThumbnailUrl);
                // クエリパラメータを除去したURLを設定
                let thumbnailUrlBase = currentThumbnailUrl;
                if (currentThumbnailUrl.includes('?')) {
                    thumbnailUrlBase = currentThumbnailUrl.split('?')[0];
                }
                setOriginalThumbnailUrl(thumbnailUrlBase);
                thumbnailUrlSet = true;
            } else if (projectUpdated && thumbnailPreview) {
                // onUpdate後の更新で、currentThumbnailUrlがnullでも、既存のサムネイルプレビューを保持
                // data:URLの場合は保持（新規アップロード中）
                // data:URLでない場合も保持（APIレスポンスにサムネイル情報が含まれていない場合）
                if (thumbnailPreview.startsWith('data:')) {
                    // FileReaderで読み込んだプレビューを保持
                    // originalThumbnailUrlはnullのまま（data:URLは一時的なもの）
                    thumbnailUrlSet = true; // 既に処理済み
                } else {
                    // 既存のサムネイルURLを保持（APIレスポンスに含まれていない場合でも）
                    // originalThumbnailUrlも更新して、hasChanges()で正しく判定できるようにする
                    // クエリパラメータをすべて除去したURLを設定（S3署名付きURLのクエリパラメータも除去）
                    let thumbnailUrlBase = thumbnailPreview;
                    if (thumbnailPreview.includes('?')) {
                        thumbnailUrlBase = thumbnailPreview.split('?')[0];
                    }
                    setOriginalThumbnailUrl(thumbnailUrlBase);
                    thumbnailUrlSet = true;
                }
            } else {
                setThumbnailPreview('');
                setOriginalThumbnailUrl(null);
                thumbnailUrlSet = true;
            }
            // originalThumbnailUrlがまだ設定されていない場合のみ設定（上記の条件分岐で既に設定済みの場合はスキップ）
            if (!thumbnailUrlSet) {
                // クエリパラメータを除去したURLを設定
                let thumbnailUrlBase = currentThumbnailUrl;
                if (currentThumbnailUrl && currentThumbnailUrl.includes('?')) {
                    thumbnailUrlBase = currentThumbnailUrl.split('?')[0];
                }
                setOriginalThumbnailUrl(thumbnailUrlBase || null);
            }
            setHasThumbnailChanged(false);
            setThumbnailFile(null);

            if (projectIdChanged) {
                lastProjectIdRef.current = project.id;
            }
            // 更新を記録
            lastProjectUpdateRef.current = projectKey;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // サムネイルのバリデーション（バックエンドのvalidators.pyに合わせて統一）
    const validateThumbnailFile = (file) => {
        if (!file) {
            return { valid: false, error: 'ファイルが選択されていません。' };
        }

        // ファイルサイズチェック（10MB以下）
        const MAX_SIZE = 10 * 1024 * 1024; // 10MB
        if (file.size > MAX_SIZE) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            return { valid: false, error: `サムネイルのファイルサイズは10MB以下にしてください。選択されたファイル: ${fileSizeMB}MB` };
        }

        // 拡張子チェック
        const fileName = file.name.toLowerCase();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'webp', 'bmp'];
        const ext = fileName.split('.').pop();
        if (!allowedExtensions.includes(ext)) {
            return { valid: false, error: `サムネイルは${allowedExtensions.join('、')}形式でなければなりません。` };
        }

        return { valid: true };
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = "";
        if (!file) return;

        const validation = validateThumbnailFile(file);
        if (!validation.valid) {
            showToast(validation.error, 'error');
            return;
        }

        setThumbnailConverting(true);
        try {
            const ready = await prepareImageFileForUpload(file);
            setThumbnailFile(ready);
            setHasThumbnailChanged(true);
            const reader = new FileReader();
            reader.onload = (ev) => {
                setThumbnailPreview(ev.target.result);
            };
            reader.readAsDataURL(ready);
        } catch (err) {
            showToast(err?.message || IMAGE_CONVERT_ERROR_MESSAGE, 'error');
        } finally {
            setThumbnailConverting(false);
        }
    };

    // 変更検知: フォームデータが変更されているかチェック
    const hasChanges = useCallback(() => {
        const dataChanged =
            formData.description !== originalFormData.description ||
            formData.use_kml !== originalFormData.use_kml ||
            formData.is_public !== originalFormData.is_public;

        // サムネイルの変更を検知
        // - ファイルが選択された（thumbnailFile !== null）
        // - 削除ボタンが押された（hasThumbnailChanged && !thumbnailFile && !thumbnailPreview）
        // - プレビューが元のURLと異なる（新しいファイルが選択された、または削除された）
        // タイムスタンプとクエリパラメータを除去して比較（キャッシュバスティング用のタイムスタンプとS3署名付きURLのクエリパラメータを無視）
        let thumbnailPreviewBase = thumbnailPreview;
        if (thumbnailPreview && thumbnailPreview.includes('?')) {
            // ?t= を除去（キャッシュバスティング用）
            if (thumbnailPreview.includes('?t=')) {
                thumbnailPreviewBase = thumbnailPreview.split('?t=')[0].split('&t=')[0];
            } else {
                // クエリパラメータを除去（S3署名付きURLなど）
                thumbnailPreviewBase = thumbnailPreview.split('?')[0];
            }
        }
        // 空文字列とnullを同じものとして扱う（どちらも「サムネイルなし」を意味する）
        // data:URLもnullとして扱う（FileReaderで読み込んだプレビューは一時的なもの）
        const previewNormalized = thumbnailPreviewBase && !thumbnailPreviewBase.startsWith('data:') ? thumbnailPreviewBase : null;
        // originalThumbnailUrlからもクエリパラメータを除去して比較
        let originalThumbnailBase = originalThumbnailUrl;
        if (originalThumbnailUrl && originalThumbnailUrl.includes('?')) {
            originalThumbnailBase = originalThumbnailUrl.split('?')[0];
        }
        const originalNormalized = originalThumbnailBase || null;
        const thumbnailChanged = hasThumbnailChanged ||
            thumbnailFile !== null ||
            (previewNormalized !== originalNormalized);

        return dataChanged || thumbnailChanged;
    }, [formData, originalFormData, thumbnailPreview, originalThumbnailUrl, hasThumbnailChanged, thumbnailFile]);

    // リセット: 元の値に戻す
    const handleReset = () => {
        if (project) {
            const resetData = {
                description: project.description || '',
                use_kml: project.use_kml || false,
                is_public: project.is_public || false
            };
            setFormData(resetData);
            setOriginalFormData(resetData);
            const thumbnailUrl = project.thumbnail_image_url || project.thumbnail_image;
            if (thumbnailUrl) {
                setThumbnailPreview(thumbnailUrl);
            } else {
                setThumbnailPreview('');
            }
            setOriginalThumbnailUrl(thumbnailUrl || null);
            setThumbnailFile(null);
            setHasThumbnailChanged(false);
        }
    };

    const copyPublicUrl = async () => {
        if (!publicUrl) return;

        try {
            const fullUrl = `${window.location.origin}/public/${publicUrl}`;
            await navigator.clipboard.writeText(fullUrl);

            setIsCopied(true);
            if (onUrlCopySuccess) {
                onUrlCopySuccess();
            }

            setTimeout(() => {
                setIsCopied(false);
            }, 3000);
        } catch (error) {
            console.error('URLコピーエラー:', error);
            if (onUrlCopyError) {
                onUrlCopyError();
            }
        }
    };

    const getPublicUrl = () => {
        if (!publicUrl) return '';
        return `${window.location.origin}/public/${publicUrl}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let updatedProject;

            // サムネイルが変更されている場合は、FormDataを使った更新エンドポイントを使用
            if (hasThumbnailChanged) {
                const payload = {
                    description: formData.description,
                    use_kml: formData.use_kml,
                    is_public: formData.is_public,
                    thumbnail_image: thumbnailFile || '' // 削除の場合は空文字列を送信
                };
                updatedProject = await updateProjectDetails(projectId, payload);
            } else {
                // サムネイルがない場合は、通常のJSON更新エンドポイントを使用
                const payload = {
                    description: formData.description,
                    use_kml: formData.use_kml,
                    is_public: formData.is_public
                };
                updatedProject = await updateProject(projectId, payload);
            }

            // サムネイルプレビューを更新（追加・削除の両方に対応）
            const newThumbnailUrl = updatedProject.thumbnail_image_url || updatedProject.thumbnail_image;

            if (newThumbnailUrl) {
                // キャッシュバスティングのためにタイムスタンプを追加
                const separator = newThumbnailUrl.includes('?') ? '&' : '?';
                const previewUrl = `${newThumbnailUrl}${separator}t=${Date.now()}`;
                setThumbnailPreview(previewUrl);
                setOriginalThumbnailUrl(newThumbnailUrl);
            } else if (hasThumbnailChanged && !thumbnailFile) {
                // 削除の場合のみプレビューをクリア
                setThumbnailPreview('');
                setOriginalThumbnailUrl(null);
            } else if (hasThumbnailChanged && thumbnailFile) {
                // 上書きの場合、サーバーからURLが返されない場合は、FileReaderで読み込んだプレビューを保持
                // しかし、originalThumbnailUrlをnullに設定して、data:URLとnullを同じものとして扱う
                // これにより、hasChanges()で正しく比較できる
                // ただし、onUpdate後にprojectが更新されるので、useEffectでthumbnailPreviewが更新されるはず
                if (!newThumbnailUrl && thumbnailPreview.startsWith('data:')) {
                    // FileReaderで読み込んだプレビューを保持
                    // originalThumbnailUrlをnullに設定して、data:URLとnullを同じものとして扱う
                    setOriginalThumbnailUrl(null);
                }
            } else {
                // サムネイルが変更されていない場合、既存のサムネイルプレビューを保持
                // APIレスポンスにサムネイル情報が含まれていない場合でも、既存のプレビューを維持する
                if (thumbnailPreview && !thumbnailPreview.startsWith('data:')) {
                    // 既存のサムネイルプレビューを保持（data:URLでない場合のみ）
                    // originalThumbnailUrlも更新して、hasChanges()で正しく判定できるようにする
                    // クエリパラメータをすべて除去したURLを設定（S3署名付きURLのクエリパラメータも除去）
                    let thumbnailUrlBase = thumbnailPreview;
                    if (thumbnailPreview.includes('?')) {
                        thumbnailUrlBase = thumbnailPreview.split('?')[0];
                    }
                    setOriginalThumbnailUrl(thumbnailUrlBase);
                }
            }

            // 公開URLを更新
            setPublicUrl(updatedProject.public_url || '');

            // 元の値を更新
            setOriginalFormData({
                description: formData.description,
                use_kml: formData.use_kml,
                is_public: formData.is_public
            });

            // サムネイルが正常に保存された場合のみ、thumbnailFileをクリア
            setThumbnailFile(null);
            setHasThumbnailChanged(false);

            // ファイル入力もリセット
            const fileInput = document.getElementById('thumbnail');
            if (fileInput) {
                fileInput.value = '';
            }

            if (onUpdate) {
                onUpdate(updatedProject);
            }

            // 公開設定が変更された場合のコールバック
            if (formData.is_public !== originalFormData.is_public) {
                if (onPublicModeSuccess) {
                    onPublicModeSuccess(updatedProject.is_public, updatedProject);
                }
            } else {
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (err) {
            console.error('プロジェクト更新エラー:', err);

            // 公開設定の変更でエラーが発生した場合
            if (formData.is_public !== originalFormData.is_public && onPublicModeError) {
                onPublicModeError();
            } else if (onError) {
                onError();
            }
        } finally {
            setLoading(false);
        }
    };

    const removeThumbnail = () => {
        setThumbnailFile(null);
        setThumbnailPreview('');
        setHasThumbnailChanged(true);
        setFormData(prev => ({ ...prev, thumbnail_image: null }));
    };

    return (
        <div className="m-0 flex w-full max-w-full flex-col p-0 text-text-primary">
            <form onSubmit={handleSubmit} className={formShell.layout2col}>
                <div className={formShell.group}>
                        <p className={formShell.label}>
                            サムネイル画像
                        </p>
                        <div className="flex flex-col items-stretch gap-4 bp-md:min-h-0 bp-md:w-full bp-md:flex-1">
                            <div className="relative flex min-h-[12.5rem] w-full items-center justify-center overflow-hidden bp-md:min-h-0 bp-md:w-full bp-md:flex-1 bp-lg:min-h-[15.625rem]">
                                {thumbnailPreview ? (
                                    <>
                                        <img
                                            src={thumbnailPreview}
                                            alt="サムネイルプレビュー"
                                            className="block h-full w-full object-cover"
                                        />
                                        <Button
                                            type="button"
                                            variant="danger"
                                            size="sm"
                                            onClick={removeThumbnail}
                                            className={thumbnailShell.removeButton}
                                            aria-label="サムネイルを削除"
                                        >
                                            削除
                                        </Button>
                                    </>
                                ) : (
                                    <div className="flex h-full min-h-[12.5rem] w-full flex-col items-center justify-center gap-3 rounded-lg border border-solid border-border bg-surface-muted text-text-muted bp-lg:min-h-[15.625rem]">
                                        <span className="material-symbols-outlined text-icon-empty text-text-muted">image</span>
                                        <span className={`text-center ${typeShell.muted}`}>画像なし</span>
                                    </div>
                                )}
                            </div>
                            <input
                                type="file"
                                id="thumbnail"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                                disabled={thumbnailConverting}
                            />
                            <label
                                htmlFor="thumbnail"
                                className={
                                    thumbnailConverting
                                        ? formShell.dropzoneDisabled
                                        : formShell.dropzone
                                }
                            >
                                <span className="material-symbols-outlined text-icon-control">upload</span>
                                {thumbnailConverting
                                    ? '画像を変換しています…'
                                    : thumbnailPreview
                                        ? '画像を変更'
                                        : '画像を選択'}
                            </label>
                        </div>
                </div>

                <div className={`${formShell.group} bp-md:flex bp-md:min-h-0 bp-md:flex-col`}>
                        <label htmlFor="description" className={formShell.label}>
                            プロジェクトの説明
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className={`${formFieldClass(false, 'textarea')} bp-md:min-h-0 bp-md:flex-1`}
                            placeholder="プロジェクトの説明を入力してください..."
                            rows="4"
                        />
                </div>

                <div className={`${formShell.form} ${formShell.fullWidth}`}>
                    <div className={formShell.section}>
                        <p className={formShell.label}>プロジェクト詳細設定</p>
                        <div className={`${formShell.grid2} items-start`}>
                            <div className={formShell.togglePanel}>
                                <FormToggle
                                    id="use_kml"
                                    name="use_kml"
                                    checked={formData.use_kml}
                                    onChange={handleInputChange}
                                    disabled={!project?.kml_data}
                                    label="KMLファイルを使用する"
                                />
                            </div>
                            <div className={formShell.togglePanel}>
                                <FormToggle
                                    id="is_public"
                                    name="is_public"
                                    checked={formData.is_public}
                                    onChange={handleInputChange}
                                    label={formData.is_public ? '公開中' : '非公開'}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={formShell.group}>
                        <label htmlFor="public-url" className={formShell.label}>
                            公開URL
                        </label>
                        <div className={formShell.urlRow}>
                            <input
                                id="public-url"
                                type="text"
                                value={formData.is_public && publicUrl ? getPublicUrl() : ''}
                                readOnly
                                className={formShell.urlInput}
                                disabled={!formData.is_public || !publicUrl}
                                placeholder={formData.is_public ? '公開URLを生成中...' : 'プロジェクトを公開するとURLが表示されます'}
                            />
                            <Button
                                onClick={copyPublicUrl}
                                variant={isCopied ? 'success' : 'primary'}
                                size="sm"
                                title={isCopied ? 'コピー完了' : 'URLをコピー'}
                                disabled={!formData.is_public || !publicUrl || isCopied}
                            >
                                <span className="material-symbols-outlined text-icon-control">
                                    {isCopied ? 'check' : 'content_copy'}
                                </span>
                            </Button>
                        </div>
                        <p className={`${formShell.hint} min-h-[1.5em]`}>
                            {formData.is_public && publicUrl
                                ? 'このURLを共有することで、誰でもプロジェクトの地図を閲覧できます。'
                                : '\u00a0'}
                        </p>
                    </div>
                </div>

                <FormActions
                    className={formShell.fullWidth}
                    hasChanges={hasChanges()}
                    loading={loading}
                    onReset={handleReset}
                    onSave={handleSubmit}
                    submitType="submit"
                />
            </form>
        </div>
    );
};

ProjectDetailForm.propTypes = {
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    project: PropTypes.object,
    onUpdate: PropTypes.func,
    onSuccess: PropTypes.func,
    onError: PropTypes.func,
    onPublicModeSuccess: PropTypes.func,
    onPublicModeError: PropTypes.func,
    onUrlCopySuccess: PropTypes.func,
    onUrlCopyError: PropTypes.func
};

export default ProjectDetailForm;
